import { useCallback, useRef, useState } from 'react';
import { trackAdminLogin } from '../services/AnalyticsService';
import AdminAuthService from '../services/AdminAuthService';
import { useFeedback } from '../context/FeedbackContext';

const LONG_PRESS_DURATION = 1500;

export const useAdminSession = ({ pageId, canOpenAdmin = true, adminBlockedMessage = '' }) => {
  const feedback = useFeedback();
  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const longPressTimerRef = useRef(null);

  const handlePressStart = useCallback((event) => {
    const target = event?.target;
    if (target?.closest?.('button, input, textarea, select, [contenteditable="true"], [data-lumirae-no-admin="true"]')) {
      return;
    }
    longPressTimerRef.current = setTimeout(() => {
      if (isEditing) return;
      if (!canOpenAdmin) {
        feedback.toast({
          type: 'info',
          title: 'Linked NFC',
          message: adminBlockedMessage || 'Open the owner NFC to edit this gift set.',
        });
        return;
      }
      setIsAdminModalOpen(true);
    }, LONG_PRESS_DURATION);
  }, [adminBlockedMessage, canOpenAdmin, feedback, isEditing]);

  const handlePressEnd = useCallback(() => clearTimeout(longPressTimerRef.current), []);

  const handleAdminVerify = useCallback((success) => {
    if (!success) return false;
    setIsEditing(true);
    setIsAdminModalOpen(false);
    if (pageId) trackAdminLogin(pageId);
    return true;
  }, [pageId]);

  const handlePasswordChanged = useCallback(async (sessionData) => {
    try {
      await AdminAuthService.establishSession(pageId, sessionData);
      setShowPasswordChange(false);
      feedback.toast({
        type: 'success',
        title: 'Password Updated',
        message: 'Your admin session is ready.',
      });
      return true;
    } catch (error) {
      await feedback.alert({
        type: 'warning',
        title: 'Session Refresh Needed',
        message: 'Password updated, but the session could not be refreshed. Please log in again.',
      });
      AdminAuthService.logoutAdmin(pageId);
      setShowPasswordChange(false);
      setIsEditing(false);
      setIsPreviewMode(false);
      setIsAdminModalOpen(true);
      return false;
    }
  }, [feedback, pageId]);

  return {
    isEditing,
    setIsEditing,
    isPreviewMode,
    setIsPreviewMode,
    isAdminModalOpen,
    setIsAdminModalOpen,
    showPasswordChange,
    setShowPasswordChange,
    handlePressStart,
    handlePressEnd,
    handleAdminVerify,
    handlePasswordChanged,
  };
};

export default useAdminSession;
